import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Calendar, Clock, CheckCircle, XCircle, 
  AlertTriangle, Loader2, MapPin, Building, Save, Plus, ExternalLink,
  RefreshCw, RotateCcw, Download, Ban, ChevronRight, Pencil, Receipt
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const VendorPaymentTab = ({
  subscription,
  setSubscription,
  selectedPlan,
  setSelectedPlan,
  processingPayment,
  setProcessingPayment,
  API_URL,
  user,
  vendorType = 'market'
}) => {
  const isMarketVendor = vendorType === 'market';
  const [showPlans, setShowPlans] = useState(false);
  // Monthly plan is now available to ALL vendor types (market, expo, festival)
  const [billingInfo, setBillingInfo] = useState({
    zipCode: '',
    country: 'South Africa',
    address: '',
    city: '',
    companyName: ''
  });
  const [savingBilling, setSavingBilling] = useState(false);
  // Payment history + last payment fetched from backend (Subscription Centre uses these)
  const [paymentHistoryFromApi, setPaymentHistoryFromApi] = useState([]);
  const [lastPaymentFromApi, setLastPaymentFromApi] = useState(null);
  // Ref to auto-scroll to Change Plan section when opened
  const changePlanRef = React.useRef(null);

  // Fetch this vendor's payment history + last payment on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/vendor/payment-history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setPaymentHistoryFromApi(data.history || []);
        setLastPaymentFromApi(data.lastPayment || null);
      } catch (e) {
        console.warn('Could not fetch payment history', e);
      }
    };
    fetchHistory();
  }, [API_URL]);

  // Scroll to Change Plan section whenever it opens
  useEffect(() => {
    if (showPlans && changePlanRef.current) {
      setTimeout(() => {
        changePlanRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [showPlans]);

  // Load billing info from user data
  useEffect(() => {
    if (user?.billing) {
      setBillingInfo({
        zipCode: user.billing.zipCode || '',
        country: user.billing.country || 'South Africa',
        address: user.billing.address || '',
        city: user.billing.city || '',
        companyName: user.billing.companyName || ''
      });
    }
  }, [user]);

  const handlePayment = async () => {
    setProcessingPayment(true);
    try {
      const response = await fetch(`${API_URL}/api/payfast/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ plan: selectedPlan })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }
      
      const data = await response.json();
      
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.payfast_url;
      
      Object.entries(data.payfast_data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        }
      });
      
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription? Your stall will remain active until the end of the current billing period.')) {
      try {
        const response = await fetch(`${API_URL}/api/payfast/cancel-subscription`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscription(prev => ({ 
            ...prev, 
            status: 'pending_cancellation',
          }));
          
          if (data.payfast_cancelled) {
            toast.success('Subscription cancelled. Your stall will remain active until the billing period ends.');
          } else {
            toast.success(data.message || 'Subscription will be cancelled at the end of the billing period.');
          }
        } else {
          const errorData = await response.json();
          toast.error(errorData.detail || 'Failed to cancel subscription');
        }
      } catch (error) {
        console.error('Cancel error:', error);
        toast.error('Failed to cancel subscription. Please try again.');
      }
    }
  };

  const handleSaveBilling = async () => {
    setSavingBilling(true);
    try {
      const response = await fetch(`${API_URL}/api/vendor/billing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(billingInfo)
      });
      
      if (response.ok) {
        toast.success('Billing information saved successfully');
        // Update local user data
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        currentUser.billing = billingInfo;
        localStorage.setItem('user', JSON.stringify(currentUser));
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save billing information');
      }
    } catch (error) {
      console.error('Save billing error:', error);
      toast.error('Failed to save billing information');
    } finally {
      setSavingBilling(false);
    }
  };

  const getPlanDisplayName = () => {
    switch (subscription.plan) {
      case 'cash': case 'manual': return 'Cash';
      case 'trial': return '7 Day Free Trial';
      case 'monthly': return 'Monthly Plan';
      case 'yearly': return 'Yearly Plan';
      default: return 'No Plan';
    }
  };

  // ----- Derived data for the Subscription Centre -----
  const planPriceLabel = () => {
    switch (subscription.plan) {
      case 'monthly': return 'R25 / month';
      case 'yearly': return 'R300 / year';
      case 'trial': return 'Free trial';
      case 'cash':
      case 'manual': return 'Cash / offline';
      default: return '—';
    }
  };

  const computeDaysRemaining = () => {
    if (subscription.daysRemaining !== undefined && subscription.daysRemaining !== null) {
      return subscription.daysRemaining;
    }
    const dateStr = subscription.nextBillingDate
      || (subscription.plan === 'trial' && subscription.startDate
          ? new Date(new Date(subscription.startDate).getTime() + (subscription.trial_duration_days || 7) * 86400000).toISOString()
          : null);
    if (!dateStr) return null;
    const diff = new Date(dateStr).getTime() - Date.now();
    return diff <= 0 ? 0 : Math.ceil(diff / 86400000);
  };

  const daysRemaining = computeDaysRemaining();
  const autoRenewalEnabled = subscription.status === 'active'
    && subscription.plan !== 'trial'
    && subscription.plan !== 'cash'
    && subscription.plan !== 'manual'
    && subscription.status !== 'pending_cancellation';

  // Overview status pill spec
  const statusSpec = (() => {
    if (subscription.status === 'active' && subscription.plan === 'trial') {
      return { label: 'Trial', bg: 'bg-blue-100', dot: 'bg-blue-500', text: 'text-blue-700' };
    }
    if (subscription.status === 'active') {
      return { label: 'Active', bg: 'bg-green-100', dot: 'bg-green-500', text: 'text-green-700' };
    }
    if (subscription.status === 'pending_cancellation') {
      return { label: 'Cancelled', bg: 'bg-red-100', dot: 'bg-red-500', text: 'text-red-700' };
    }
    if (subscription.status === 'payment_failed' || subscription.status === 'failed') {
      return { label: 'Payment Failed', bg: 'bg-red-100', dot: 'bg-red-500', text: 'text-red-700' };
    }
    if (subscription.status === 'cancelled') {
      return { label: 'Expired', bg: 'bg-gray-200', dot: 'bg-gray-400', text: 'text-gray-700' };
    }
    return { label: 'Inactive', bg: 'bg-gray-200', dot: 'bg-gray-400', text: 'text-gray-700' };
  })();

  const lastPayment = lastPaymentFromApi || subscription.lastPayment || null;
  const paymentHistory = paymentHistoryFromApi.length > 0
    ? paymentHistoryFromApi
    : (Array.isArray(subscription.paymentHistory) ? subscription.paymentHistory : []);

  const fmtDate = (d, opts) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-ZA', opts || { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const fmtAmount = (amt) => amt !== undefined && amt !== null ? `R${Number(amt).toFixed(2)}` : '—';

  // Billing display mode / edit mode toggle
  const billingComplete = !!(billingInfo.address && billingInfo.city && billingInfo.zipCode);
  const [editingBilling, setEditingBilling] = useState(false);

  // Actions available (conditional on state)
  //  Renew — always visible. If already active, it re-opens the plans so vendor can upgrade/refresh.
  //  Retry — only when the last payment failed.
  //  Download Invoice — only when we have an invoice URL from PayFast/our system.
  //  Cancel — only when currently paying (not cash/manual).
  const canRenew = true;
  const canRetry = lastPayment?.status === 'failed';
  const canDownloadInvoice = !!(lastPayment && (lastPayment.id || lastPayment.receiptUrl));
  const canCancel = subscription.status === 'active' && subscription.plan !== 'cash' && subscription.plan !== 'manual';

  // Handler for the "Renew Subscription" action — always opens the plan picker.
  const handleRenewClick = () => {
    setShowPlans(true);
  };

  // Handler for "Retry Failed Payment" — re-initiates PayFast checkout.
  const handleRetryClick = () => {
    if (!canRetry) {
      toast.info('No failed payment to retry.');
      return;
    }
    handlePayment();
  };

  // Helper to open our internal receipt HTML endpoint in a new tab.
  // The endpoint requires auth via `?token=` query since window.open cannot send headers.
  const openReceipt = (payment) => {
    const url = payment?.receiptUrl || (payment?.id ? `/api/vendor/payments/${payment.id}/receipt` : null);
    if (!url) {
      toast.info('Receipt not available yet. It will appear once the payment record is finalised.');
      return;
    }
    const token = localStorage.getItem('token');
    const full = `${API_URL}${url}?token=${encodeURIComponent(token || '')}`;
    window.open(full, '_blank', 'noopener,noreferrer');
  };

  // Handler for "Download Invoice"
  const handleInvoiceClick = () => {
    if (!lastPayment) {
      toast.info('No invoice available yet. Invoices become available after your first PayFast transaction.');
      return;
    }
    openReceipt(lastPayment);
  };

  // Timeline milestones (only mark reached ones as "complete")
  const timelineData = [
    { key: 'created', label: 'Account Created', date: user?.created_at, reached: !!user?.created_at },
    { key: 'started', label: 'Subscription Started', date: subscription.startDate, reached: !!subscription.startDate },
    { key: 'paid', label: 'Payment Received', date: lastPayment?.date || (subscription.status === 'active' ? subscription.startDate : null), reached: !!(lastPayment?.date || (subscription.status === 'active' && subscription.plan !== 'trial')) },
    { key: 'active', label: 'Subscription Active', date: subscription.startDate, reached: subscription.status === 'active' && subscription.plan !== 'trial' },
  ];

  const getEndDate = () => {
    if (subscription.nextBillingDate) {
      return new Date(subscription.nextBillingDate).toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    if (subscription.plan === 'trial' && subscription.startDate) {
      const durationDays = subscription.trial_duration_days || 7;
      const trialEnd = new Date(new Date(subscription.startDate).getTime() + durationDays * 24 * 60 * 60 * 1000);
      return trialEnd.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    return null;
  };

  return (
    <div className="space-y-6 mt-4" data-testid="vendor-subscription-centre">
      {/* Intro heading */}
      <div>
        <h3 className="text-2xl font-bold text-[#252525] dark:text-white">Subscription Overview</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Manage your subscription, billing details and view your payment history.
        </p>
      </div>

      {/* ============ Section 1 · Overview | Last Payment | Actions ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ---- Subscription Overview ---- */}
        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-gray-700 p-6 flex flex-col"
          data-testid="subscription-overview-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-[#252525] dark:text-white">Subscription Status</span>
              <span className={`inline-flex items-center gap-1.5 ${statusSpec.bg} ${statusSpec.text} px-2.5 py-0.5 rounded-full text-xs font-medium`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusSpec.dot}`} />
                {statusSpec.label}
              </span>
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${statusSpec.bg}`}>
              {subscription.status === 'active'
                ? <CheckCircle className="w-5 h-5 text-green-600" />
                : subscription.status === 'failed'
                  ? <XCircle className="w-5 h-5 text-red-600" />
                  : <Clock className="w-5 h-5 text-gray-500" />}
            </div>
          </div>

          <h4 className="text-2xl font-bold text-[#252525] dark:text-white leading-tight">
            {getPlanDisplayName()}
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{planPriceLabel()}</p>

          <div className="flex flex-col gap-3 mt-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
                <Calendar className="w-3.5 h-3.5" />
                Next Renewal
              </div>
              <div className="text-sm font-semibold text-[#252525] dark:text-white">
                {getEndDate() || '—'}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
                <Clock className="w-3.5 h-3.5" />
                Days Remaining
              </div>
              <div className="text-sm font-semibold text-green-600">
                {daysRemaining !== null ? `${daysRemaining} Days` : '—'}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
                <RefreshCw className="w-3.5 h-3.5" />
                Auto Renewal
              </div>
              <div className={`text-sm font-semibold ${autoRenewalEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                {autoRenewalEnabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>

          <Button
            onClick={() => setShowPlans(!showPlans)}
            variant="outline"
            className="mt-auto w-full border-2 border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525] rounded-xl font-semibold py-3"
            data-testid="change-plan-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {showPlans ? 'Close plans' : 'Change Plan'}
          </Button>

          {/* Inline "Choose a Plan" — opens inside the Subscription Status card */}
          {showPlans && (
            <div ref={changePlanRef} className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-4 scroll-mt-4" data-testid="change-plan-section">
              <h4 className="text-base font-semibold text-[#252525] dark:text-white">Choose a Plan</h4>
              <div className="grid grid-cols-1 gap-3">
                <div
                  onClick={() => setSelectedPlan('monthly')}
                  className={`relative cursor-pointer p-4 rounded-xl border-2 transition-all min-w-0 ${
                    selectedPlan === 'monthly'
                      ? 'border-[#FFD12D] bg-[#FFD12D]/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-[#FFD12D]/50'
                  }`}
                >
                  {selectedPlan === 'monthly' && (
                    <div className="absolute top-2 right-2"><CheckCircle className="w-4 h-4 text-[#FFD12D]" /></div>
                  )}
                  <div className="flex items-center gap-3 pr-6">
                    <div className="w-10 h-10 bg-[#FFD12D]/30 rounded-full flex items-center justify-center flex-shrink-0"><CreditCard className="w-5 h-5 text-[#b8860b] dark:text-[#FFD12D]" /></div>
                    <div className="min-w-0 flex-1">
                      <h5 className="font-bold text-sm text-[#252525] dark:text-white leading-tight">Monthly</h5>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Billed monthly</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-baseline justify-end">
                        <span className="text-xl font-bold text-[#252525] dark:text-white">R25</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">/mo</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => setSelectedPlan('yearly')}
                  className={`relative cursor-pointer p-4 rounded-xl border-2 transition-all min-w-0 ${
                    selectedPlan === 'yearly'
                      ? 'border-[#FFD12D] bg-[#FFD12D]/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-[#FFD12D]/50'
                  }`}
                >
                  {selectedPlan === 'yearly' && (
                    <div className="absolute top-2 right-2"><CheckCircle className="w-4 h-4 text-[#FFD12D]" /></div>
                  )}
                  <div className="flex items-center gap-3 pr-6">
                    <div className="w-10 h-10 bg-green-500/30 rounded-full flex items-center justify-center flex-shrink-0"><Calendar className="w-5 h-5 text-green-700 dark:text-green-400" /></div>
                    <div className="min-w-0 flex-1">
                      <h5 className="font-bold text-sm text-[#252525] dark:text-white leading-tight">Yearly</h5>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">R300 billed yearly</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-baseline justify-end">
                        <span className="text-xl font-bold text-[#252525] dark:text-white">R25</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">/mo</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  onClick={async () => {
                    await handleSaveBilling();
                    const planDiffers = selectedPlan && selectedPlan !== (subscription.plan || subscription.subscription);
                    if (planDiffers || subscription.status !== 'active') {
                      await handlePayment();
                    }
                  }}
                  disabled={savingBilling || processingPayment}
                  className="w-full bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold rounded-xl py-3"
                  data-testid="vendor-payment-submit"
                >
                  {processingPayment
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
                    : <><CreditCard className="w-4 h-4 mr-2" />{subscription.status === 'active' ? 'Update plan' : 'Subscribe'} — {selectedPlan === 'monthly' ? 'R25/month' : 'R300/year'}</>
                  }
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ---- Last Payment ---- */}
        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-gray-700 p-6 flex flex-col"
          data-testid="last-payment-card"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-[#252525] dark:text-white">Last Payment</span>
            {lastPayment ? (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                lastPayment.status === 'failed'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${lastPayment.status === 'failed' ? 'bg-red-500' : 'bg-green-500'}`} />
                {lastPayment.status === 'failed' ? 'Failed' : 'Successful'}
              </span>
            ) : (
              <span className="text-xs text-gray-400">No payments yet</span>
            )}
          </div>

          <h4 className="text-3xl font-bold text-[#252525] dark:text-white">
            {lastPayment ? fmtAmount(lastPayment.amount) : '—'}
          </h4>

          <div className="space-y-4 mt-5 mb-5 text-sm">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Payment Method</div>
              <div className="font-medium text-[#252525] dark:text-white mt-0.5">
                {lastPayment?.method || 'PayFast'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Reference</div>
              <div className="font-mono text-xs text-[#252525] dark:text-white mt-0.5 break-all">
                {lastPayment?.reference || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Date</div>
              <div className="font-medium text-[#252525] dark:text-white mt-0.5">
                {lastPayment?.date ? fmtDate(lastPayment.date, { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
              </div>
            </div>
          </div>

          <Button
            onClick={() => {
              if (!lastPayment) {
                toast.info('Receipt not available yet. Once your first payment clears via PayFast, receipts will appear here.');
                return;
              }
              openReceipt(lastPayment);
            }}
            variant="outline"
            disabled={!lastPayment}
            className="mt-auto w-full border-2 border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-semibold py-3"
            data-testid="view-receipt-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            View Receipt
          </Button>
        </div>

        {/* ---- Actions ---- */}
        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-gray-700 p-6 flex flex-col"
          data-testid="actions-card"
        >
          <h4 className="text-sm font-semibold text-[#252525] dark:text-white mb-4">Actions</h4>

          <div className="space-y-3">
            {/* Renew */}
            <button
              type="button"
              onClick={handleRenewClick}
              disabled={processingPayment}
              data-testid="action-renew"
              className={`w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-[#FFD12D] transition-colors text-left hover:bg-[#FFD12D]/5 ${processingPayment ? 'opacity-40 cursor-wait' : ''}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#FFD12D]/20 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-4 h-4 text-[#FFD12D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#252525] dark:text-white">Renew Subscription</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Renew your subscription now</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>

            {/* Retry */}
            <button
              type="button"
              onClick={handleRetryClick}
              disabled={!canRetry || processingPayment}
              data-testid="action-retry"
              className={`w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-red-400 transition-colors text-left ${!canRetry ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50 dark:hover:bg-red-900/20'}`}
            >
              <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#252525] dark:text-white">Retry Failed Payment</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Retry your last failed payment</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>

            {/* Download Invoice */}
            <button
              type="button"
              onClick={handleInvoiceClick}
              data-testid="action-invoice"
              className={`w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-blue-400 transition-colors text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 ${!canDownloadInvoice ? 'opacity-60' : ''}`}
            >
              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#252525] dark:text-white">Download Invoice</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Download your latest invoice</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>

            {/* Cancel */}
            <button
              type="button"
              onClick={() => {
                if (!canCancel) {
                  if (subscription.plan === 'cash' || subscription.plan === 'manual') {
                    toast.info('Cash / manual subscriptions are managed offline. Contact support to cancel.');
                  } else {
                    toast.info('No active subscription to cancel.');
                  }
                  return;
                }
                handleCancelSubscription();
              }}
              data-testid="action-cancel"
              className={`w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-red-400 transition-colors text-left hover:bg-red-50 dark:hover:bg-red-900/20 ${!canCancel ? 'opacity-60' : ''}`}
            >
              <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Ban className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#252525] dark:text-white">Cancel Subscription</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Cancel your subscription</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>
          </div>
        </div>
      </div>

      {/* ============ Section 4/5 · Payment History + Billing Details ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Payment History — spans 2 columns on lg */}
        <div
          className="lg:col-span-2 bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-gray-700 p-6"
          data-testid="payment-history-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-[#252525] dark:text-white">Payment History</h4>
            {paymentHistory.length > 4 && (
              <button type="button" className="text-xs px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50">
                View All
              </button>
            )}
          </div>

          {paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
              <Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No payment history yet. Once a PayFast payment is processed it will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <th className="text-left font-medium py-2 pr-3">Date</th>
                    <th className="text-left font-medium py-2 pr-3">Amount</th>
                    <th className="text-left font-medium py-2 pr-3">Status</th>
                    <th className="text-left font-medium py-2 pr-3">Reference</th>
                    <th className="text-left font-medium py-2 pr-3">Method</th>
                    <th className="text-left font-medium py-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.slice(0, 6).map((p, i) => {
                    const isFailed = p.status === 'failed';
                    return (
                      <tr key={p.reference || i} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                        <td className="py-3 pr-3 text-[#252525] dark:text-white">{fmtDate(p.date)}</td>
                        <td className="py-3 pr-3 font-medium text-[#252525] dark:text-white">{fmtAmount(p.amount)}</td>
                        <td className="py-3 pr-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${isFailed ? 'text-red-600' : 'text-green-600'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isFailed ? 'bg-red-500' : 'bg-green-500'}`} />
                            {isFailed ? 'Failed' : 'Successful'}
                          </span>
                        </td>
                        <td className="py-3 pr-3 font-mono text-xs text-gray-600 dark:text-gray-400">{p.reference || '—'}</td>
                        <td className="py-3 pr-3 text-gray-600 dark:text-gray-400">{p.method || 'PayFast'}</td>
                        <td className="py-3 pr-3">
                          <button
                            type="button"
                            onClick={() => {
                              if (isFailed) handlePayment();
                              else openReceipt(p);
                            }}
                            className={`text-xs font-semibold hover:underline ${isFailed ? 'text-red-600' : 'text-blue-600'}`}
                          >
                            {isFailed ? 'Retry' : 'View'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Billing Details */}
        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-gray-700 p-6"
          data-testid="billing-details-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-[#252525] dark:text-white">Billing Details</h4>
            <button
              type="button"
              onClick={() => setEditingBilling(!editingBilling)}
              className="text-xs px-3 py-1 rounded-full border border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525] font-semibold flex items-center gap-1"
              data-testid="edit-billing-toggle"
            >
              <Pencil className="w-3 h-3" />
              {editingBilling ? 'Close' : 'Edit'}
            </button>
          </div>

          {!editingBilling ? (
            <dl className="space-y-3 text-sm">
              {[
                ['Business Name', billingInfo.companyName || user?.name || '—'],
                ['Address', billingInfo.address || '—'],
                ['City', billingInfo.city || '—'],
                ['Country', billingInfo.country || '—'],
                ['ZIP Code', billingInfo.zipCode || '—'],
                ['VAT Number', billingInfo.vatNumber || '—'],
                ['Email', user?.email || '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{k}</dt>
                  <dd className="text-[#252525] dark:text-white font-medium text-right break-all">{v}</dd>
                </div>
              ))}
              {!billingComplete && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                  Complete your billing details to enable proper invoicing.
                </p>
              )}
            </dl>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">ZIP Code</Label>
                  <Input value={billingInfo.zipCode} onChange={(e) => setBillingInfo({ ...billingInfo, zipCode: e.target.value })} placeholder="0184" className="rounded-xl h-10 dark:bg-[#1a1a1a] dark:text-white" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Country</Label>
                  <Select value={billingInfo.country} onValueChange={(v) => setBillingInfo({ ...billingInfo, country: v })}>
                    <SelectTrigger className="rounded-xl h-10 dark:bg-[#1a1a1a] dark:text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="South Africa">South Africa</SelectItem>
                      <SelectItem value="Namibia">Namibia</SelectItem>
                      <SelectItem value="Botswana">Botswana</SelectItem>
                      <SelectItem value="Zimbabwe">Zimbabwe</SelectItem>
                      <SelectItem value="Mozambique">Mozambique</SelectItem>
                      <SelectItem value="Lesotho">Lesotho</SelectItem>
                      <SelectItem value="Eswatini">Eswatini</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Address</Label>
                <Input value={billingInfo.address} onChange={(e) => setBillingInfo({ ...billingInfo, address: e.target.value })} placeholder="Street address" className="rounded-xl h-10 dark:bg-[#1a1a1a] dark:text-white" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">City</Label>
                <Input value={billingInfo.city} onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })} placeholder="City" className="rounded-xl h-10 dark:bg-[#1a1a1a] dark:text-white" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Company / Business Name</Label>
                <Input value={billingInfo.companyName} onChange={(e) => setBillingInfo({ ...billingInfo, companyName: e.target.value })} placeholder="Business name" className="rounded-xl h-10 dark:bg-[#1a1a1a] dark:text-white" />
              </div>
              <Button
                onClick={async () => {
                  await handleSaveBilling();
                  setEditingBilling(false);
                }}
                disabled={savingBilling}
                className="w-full mt-2 bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold rounded-xl"
                data-testid="vendor-billing-save-btn"
              >
                {savingBilling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Details</>}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ============ Section 6 · Subscription Timeline ============ */}
      <div
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-gray-700 p-6"
        data-testid="subscription-timeline-card"
      >
        <h4 className="text-base font-semibold text-[#252525] dark:text-white mb-6">Subscription Timeline</h4>

        <div className="relative">
          {/* Horizontal connecting line — behind the dots on md+ */}
          <div className="hidden md:block absolute top-4 left-8 right-8 h-0.5 bg-gray-200 dark:bg-gray-700" />

          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-2 relative">
            {timelineData.map((t) => (
              <li key={t.key} className="flex md:flex-col gap-3 items-start md:items-center text-center md:text-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                    t.reached
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                  }`}
                >
                  {t.reached ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                </div>
                <div className="text-left md:text-center min-w-0">
                  <div className="text-sm font-semibold text-[#252525] dark:text-white truncate">
                    {t.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t.reached ? fmtDate(t.date) : 'Pending'}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>


      {/* ============ Footer ============ */}
      <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center flex items-center justify-center gap-2 flex-wrap">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          All payments are securely processed by PayFast. Your subscription will auto-renew unless cancelled.
        </p>
      </div>
    </div>
  );
};

export default VendorPaymentTab;
